<script setup lang="ts">
import type { IKYCLevel } from "@/app/shared/types/KYCLevels.type"
import type { IWallet } from "@/user-account/types/UserAccount.type"
import { fetchUserKycLevels } from "@/app/api"
import PersonalInfoModal from "@/app/components/modal/PersonalInfoModal.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useBonuses } from "@/app/composables/useBonus"
import { useConfig } from "@/app/composables/useConfig"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { useFreeSpins } from "@/app/composables/useFreeSpins"
import { t } from "@/app/composables/useI18n"
import ConfirmationModal from "@/app/shared/components/ConfirmationModal.vue"
import { CKYC_STATUSES } from "@/app/shared/types/KYCLevels.type"
import { toast } from "@/app/utils/notification"
import { useGameHelper } from "@/casino/composables/useGameHelper"
import { useGameStore } from "@/casino/store/game.store"
import { api } from "@/services"
import { useUserService } from "@/user-account/composables/useUserService"
import moment from "moment"
import { storeToRefs } from "pinia"
import { computed, onMounted, ref } from "vue"
import { useBetStore } from "../../../../../store"
import ActiveBlock from "../CasinoBonusSection/ActiveBlock.vue"
import FreeSpin from "../CasinoBonusSection/FreeSpin.vue"
import MoneyTransferSelection from "./MoneyTransferSelection.vue"
import Transaction from "./Transaction.vue"

defineProps({
  show: Boolean,
})

defineEmits(["close"])

const { systemSettings } = useConfig()
const { wallets, activeWallet, user, selectWallet } = useUserService()
const { getIconByTicker } = useCurrencies()
const { isGameSession } = useGameHelper()
const { activeBonus } = useBonuses()
const showConfirmationModal = ref(false)
const showPersonalInfoModal = ref(false)
const tempWallet = ref<IWallet | null>(null)
const tempPrevSelectedWalletId = ref<number | null>(null)
const { startGame } = useGameHelper()
const gameStore = useGameStore()
const { getGameState } = storeToRefs(gameStore)
const { freeSpins } = useFreeSpins()
const kycLevels = ref<IKYCLevel[]>([])

const store = useBetStore()

const tabs = computed(() => [
  { value: "balance", title: t("finance.balance"), icon: "balance" },
  { value: "deposit", title: t("finance.deposit"), icon: "deposit" },
  { value: "withdraw", title: t("finance.withdraw"), icon: "withdraw" },
  {
    value: "transactions",
    title: t("finance.transactions"),
    icon: "transaction",
  },
])

const selectedTab = computed(() => {
  return store.getWalletTab
})

function onTabClick(tab) {
  store.setWalletTab(tab)
}

function navigatePersonalInfo() {
  showPersonalInfoModal.value = true
}

async function setActiveWallet(wallet: IWallet) {
  tempWallet.value = wallet
  tempPrevSelectedWalletId.value = activeWallet.value?.id
  selectWallet(wallet.id)
  if (isGameSession.value) {
    showConfirmationModal.value = true
    return
  }

  await api.finance.lastSelectedId({ walletId: wallet.id })
}

function getActiveBonusExpireDate(bonus) {
  if (bonus?.expirationDate !== null && bonus?.expirationDate !== "") {
    return `${t("casino.expired_at")} ${moment(bonus?.expirationDate).format(
      "HH:mm DD/MM/YYYY",
    )}`
  }
  else {
    return null
  }
}

async function loadKycData(): Promise<void> {
  try {
    const { data } = await fetchUserKycLevels()
    kycLevels.value = data.data || []
  }
  catch (error) {
    console.error("Failed to load KYC levels:", error)
  }
}

const isLevel1Completed = computed(() => {
  const level1 = kycLevels.value.find(level => level.level === 1)
  return level1?.status === CKYC_STATUSES.APPROVED
})

const depositPersonalInfoRequirement = computed(() => {
  return (
    systemSettings.value.kycLevels.level_1.required_before_deposit === true
    && !isLevel1Completed.value
  )
})

const withdrawPersonalInfoRequirement = computed(() => {
  return (
    systemSettings.value.personalInformationRequiredFor === "withdrawal"
    && !isLevel1Completed.value
  )
})

function onFreeSpinClick() {
  store.handleShowBetModal(false)
}

async function handleConfirmModal(confirmed: boolean) {
  store.handleShowBetModal(false)
  if (confirmed) {
    try {
      if (getGameState.value) {
        await startGame(
          getGameState.value.game,
          getGameState.value.isDemo,
          store.handleShowLogin,
        )
        await api.finance.lastSelectedId({ walletId: tempWallet.value?.id })
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred"
      console.error(errorMessage)
      selectWallet(tempPrevSelectedWalletId.value)
      toast.error(
        errorMessage,
      )
    }
    showConfirmationModal.value = false
  }
  else {
    selectWallet(tempPrevSelectedWalletId.value)
  }
}

function onPersonalInfoModalClose() {
  showPersonalInfoModal.value = false
}

function onPersonalInfoModalSuccess() {
  showPersonalInfoModal.value = false
  // Reload KYC data after successful submission
  loadKycData()
}

onMounted(() => {
  loadKycData()
})
</script>

<template>
  <div>
    <div class="wallets">
      <div
        v-for="(tab, i) in tabs"
        :key="i"
        :class="`wallet ${selectedTab === tab.value && 'active-wallet'}`"
        @click="onTabClick(tab.value)"
      >
        <BaseIcon
          class="wallet-tab"
          :name="tab.icon"
          size="18"
          :color="
            selectedTab === tab.value
              ? 'var(--be-primary-btn-text-label)'
              : 'var( --be-primary-text-color-opacity)'
          "
        />
        <div>
          {{ tab.title }}
        </div>
      </div>
    </div>
    <div v-if="selectedTab === 'balance'" class="balance">
      <div>
        <div class="wallet-balance">
          <div v-for="wallet in wallets" :key="wallet.id" class="balance-row">
            <div>
              <label class="checkbox-container" :class="wallet.status === 'disabled' ? 'cursor-not-allowed' : ''">
                <template v-if="wallet.status === 'disabled'">
                  <BaseIcon
                    class="checkmark lock-icon"
                    name="lock-outline"
                    size="18"
                    color="var(--be-accent-text-color)"
                  />
                  <span class="disabled-text">{{ wallet?.balance }}</span>
                </template>
                <template v-else>
                  <input

                    type="radio"
                    :checked="activeWallet?.id === wallet.id"
                    @click="() => setActiveWallet(wallet)"
                  >
                  <span>{{ wallet?.balance }}</span>
                  <span class="checkmark" />
                </template>
              </label>
            </div>
            <div class="currency-balance" :class="wallet.status === 'disabled' ? 'disabled-text' : ''">
              <span v-if="wallet.type === 'bonus'">BONUS</span>
              <div>{{ wallet?.currency?.name }}</div>
              <img
                v-if="wallet?.currency?.image_url"
                :src="wallet.currency.image_url"
                class="currency-icon"
                alt="Currency Icon"
              >
              <img
                v-else
                :src="getIconByTicker(wallet?.currency?.ticker)"
                class="currency-icon"
                alt="Currency Icon"
              >
            </div>
          </div>
        </div>
        <ActiveBlock
          v-if="activeBonus !== null"
          class="block"
          :bonus="activeBonus"
          :title="activeBonus.name"
          :expire="getActiveBonusExpireDate(activeBonus)"
          :value="`${activeBonus?.initialAmount} ${activeBonus?.currency?.ticker}`"
          :icon="getIconByTicker(activeBonus?.currency?.ticker)"
        />

        <FreeSpin
          v-for="freeSpin in freeSpins"
          :key="freeSpin.id"
          :free-spin="freeSpin"
          class="block"
          @click="onFreeSpinClick"
        />
      </div>
    </div>
    <div v-if="selectedTab === 'deposit'" class="deposit">
      <div v-if="depositPersonalInfoRequirement" class="kyc-action">
        <p>{{ t("kyc.deposit_kyc_message") }}</p>
        <BaseButton @click="navigatePersonalInfo">
          {{ t("kyc.сlick_to_complete") }}
        </BaseButton>
      </div>
      <MoneyTransferSelection v-else type="deposit" />
    </div>
    <div v-if="selectedTab === 'withdraw'" class="withdraw">
      <div v-if="user.status === 'kyc-failed'" class="kyc-failed-status">
        {{ t("kyc.status_failed") }}
      </div>
      <div v-else-if="withdrawPersonalInfoRequirement" class="kyc-action">
        <p>{{ t("kyc.withdraw_kyc_message") }}</p>
        <BaseButton @click="navigatePersonalInfo">
          {{ t("kyc.сlick_to_complete") }}
        </BaseButton>
      </div>
      <div v-else-if="user?.kycWithdrawalBlocked">
        <div v-if="user?.kycStatus === 'pending'">
          <p v-if="user?.countBlockedWithdrawals === 0">
            {{ t("kyc.verify_identity_pending_message") }}
          </p>
          <p v-else>
            {{ t("kyc.verify_identity_pending_blocked_withdrawals_message") }}
          </p>
        </div>
        <div v-else class="kyc-action">
          <p>{{ t("kyc.verify_identity_message") }}</p>
          <BaseButton @click="api.kyc.redirect()">
            {{ t("kyc.verify_identity") }}
          </BaseButton>
        </div>
      </div>
      <div v-else-if="!user?.withdrawal" class="kyc-action">
        {{ t("finance.withdrawals_not_posible_message") }}
      </div>
      <MoneyTransferSelection v-else type="withdraw" />
    </div>
    <div v-if="selectedTab === 'transactions'">
      <Transaction />
    </div>
    <ConfirmationModal
      :show="showConfirmationModal"
      :message="t('casino.restartGameSessionConfirmationText')"
      @confirm-handler="handleConfirmModal"
    />
    <PersonalInfoModal
      :show="showPersonalInfoModal"
      @close="onPersonalInfoModalClose"
      @success="onPersonalInfoModalSuccess"
    />
  </div>
</template>

<style lang="scss" scoped>
.currency-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  fill: white;
}
.wallet-balance {
  padding: 16px;
  background: var(--be-big-card-bg);
  border-radius: 8px;
  margin: 16px 0px;
  .balance-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .currency-balance {
    display: flex;
    gap: 6px;
    font-family: Poppins-reg;
    font-size: 14px;
    font-style: normal;
  }
}

.block {
  margin-bottom: 16px;
}

.detail {
  border-bottom: 1px solid var(--be-small-card-bg);

  .wallets {
    display: flex;
    gap: 0 16px;
    justify-content: center;
    border-radius: 8px;
    background: var(--be-big-card-bg);
    padding: 10px;
    margin: 16px;
  }

  .wallet-tab {
    margin-bottom: 6px;
  }

  .wallet {
    color: rgba(255, 255, 255, 0.3);
    width: 122px;
    height: 58px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    font-size: 11px;
    cursor: pointer;
  }

  .active-wallet {
    color: var(--be-primary-btn-label);
    background: var(--be-primary-btn);
  }
  .detail-padding {
    display: flex;
    justify-content: space-between;
    padding: 10px 16px;
  }
}

.balance {
  margin: 0 16px;
}

.detail-option-title {
  display: flex;
  gap: 0 8px;
}

h2 {
  width: 100%;
  text-align: center;
  border-bottom: 1px solid #cdcdcd;
  color: white;
  line-height: 0px;
  margin: 10px 0 20px;
}
h2 span {
  background: var(--be-big-card-bg);
  font-size: 14px;
  padding: 0 16px;
}
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 55px;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(13, 12, 44, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 450px;
  margin-left: auto;
  // padding: 20px 20px;
  border-radius: 20px;
  background: #0d0c2c;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  position: relative;
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
  border-radius: 8px;
  background: var(--be-big-card-bg);
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  margin: 0 16px;
  padding: 10px;
}

.account-detail__options {
  display: flex;
}

.deposit,
.withdraw {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  margin: 16px;
  padding: 16px;
}

.kyc-action {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.kyc-failed-status {
  color: var(--be-accent-text-color);
  font-size: 14px;
}

.checkmark.lock-icon {
  background: transparent;
  border-color: transparent
}

.disabled-text {
  color: var(--be-primary-text-color-opacity);
  opacity: 0.5;
}
</style>
